import { Link, useNavigate, NavLink, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useNavbarScroll } from '../../hooks/useNavbarScroll'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../i18n/LanguageContext'
import { supabase } from '../../config/supabase'
import UserMenu from './UserMenu'
import './Navbar.css'

function Navbar() {
  const { user } = useAuth()
  const { t, language, toggleLanguage } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const navbarRef = useNavbarScroll()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    setIsAuthenticated(!!user)
  }, [user])

  useEffect(() => {
    // Cerrar menú móvil cuando cambia la ruta
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    // Prevenir scroll del body cuando el menú móvil está abierto
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut()
      localStorage.removeItem('token')
      setIsAuthenticated(false)
      setIsMobileMenuOpen(false)
      window.dispatchEvent(new Event('authChange'))
      navigate('/')
    } catch (error) {
      console.error('Error logging out:', error)
      // Limpiar localmente incluso si hay error
      localStorage.removeItem('token')
      setIsAuthenticated(false)
      setIsMobileMenuOpen(false)
      navigate('/')
    }
  }

  const handleNavClick = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <>
      <nav className="navbar" ref={navbarRef}>
        <div className="navbar-container">
          <Link to="/" className="navbar-logo">
            <img src="/img/logo-prueba.png" alt="The UNFILTERED COLLECTIVE" className="logo-img" />
          </Link>
          
          <div className="navbar-links">
            <NavLink 
              to="/" 
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              {t('nav.home')}
            </NavLink>
            <NavLink 
              to="/about-us" 
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              {t('nav.aboutUs')}
            </NavLink>
            <NavLink 
              to="/contact" 
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              {t('nav.contact')}
            </NavLink>
          </div>
          
          <div className="navbar-right">
            <div 
              className={`lang-toggle-pill ${language}`}
              role="group"
              aria-label={language === 'en' ? 'Cambiar a español' : 'Switch to English'}
              title={language === 'en' ? t('nav.translateToSpanish') : t('nav.translateToEnglish')}
            >
              <button
                type="button"
                className={`lang-option ${language === 'en' ? 'active' : ''}`}
                onClick={() => language !== 'en' && toggleLanguage()}
                aria-pressed={language === 'en'}
                aria-label="English"
              >
                {t('nav.langEn')}
              </button>
              <button
                type="button"
                className={`lang-option ${language === 'es' ? 'active' : ''}`}
                onClick={() => language !== 'es' && toggleLanguage()}
                aria-pressed={language === 'es'}
                aria-label="Español"
              >
                {t('nav.langEs')}
              </button>
              <span className="lang-toggle-slider" aria-hidden="true" />
            </div>
            {isAuthenticated ? (
              <>
                <div className="navbar-desktop-menu">
                  <UserMenu onLogout={handleLogout} />
                </div>
                <button 
                  className="mobile-menu-toggle"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label={t('nav.toggleMenu')}
                >
                  <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </button>
              </>
            ) : (
              <>
                <div className="auth-buttons">
                  <Link to="/login" className="btn-login">{t('nav.login')}</Link>
                  <Link to="/register" className="btn-register">{t('nav.register')}</Link>
                </div>
                <button 
                  className="mobile-menu-toggle"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label={t('nav.toggleMenu')}
                >
                  <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <div 
          className={`mobile-menu-sidebar ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            className="mobile-menu-close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label={t('nav.closeMenu')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path 
                d="M18 6L6 18M6 6L18 18" 
                stroke="var(--color-dorado)" 
                strokeWidth="2" 
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div 
              className={`lang-toggle-pill lang-toggle-pill-mobile ${language}`}
              role="group"
              aria-label={language === 'en' ? 'Cambiar a español' : 'Switch to English'}
              title={language === 'en' ? t('nav.translateToSpanish') : t('nav.translateToEnglish')}
            >
              <button
                type="button"
                className={`lang-option ${language === 'en' ? 'active' : ''}`}
                onClick={() => language !== 'en' && toggleLanguage()}
                aria-pressed={language === 'en'}
                aria-label="English"
              >
                {t('nav.langEn')}
              </button>
              <button
                type="button"
                className={`lang-option ${language === 'es' ? 'active' : ''}`}
                onClick={() => language !== 'es' && toggleLanguage()}
                aria-pressed={language === 'es'}
                aria-label="Español"
              >
                {t('nav.langEs')}
              </button>
              <span className="lang-toggle-slider" aria-hidden="true" />
            </div>
          <nav className="mobile-nav-links">
            {!isAuthenticated ? (
              <>
                <NavLink 
                  to="/" 
                  className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  {t('nav.home')}
                </NavLink>
                <NavLink 
                  to="/about-us" 
                  className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  {t('nav.aboutUs')}
                </NavLink>
                <NavLink 
                  to="/contact" 
                  className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  {t('nav.contact')}
                </NavLink>
                <div className="mobile-auth-buttons">
                  <Link 
                    to="/login" 
                    className="mobile-btn-login"
                    onClick={handleNavClick}
                  >
                    {t('nav.login')}
                  </Link>
                  <Link 
                    to="/register" 
                    className="mobile-btn-register"
                    onClick={handleNavClick}
                  >
                    {t('nav.register')}
                  </Link>
                </div>
              </>
            ) : (
              <>
                <NavLink 
                  to="/" 
                  className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  {t('nav.home')}
                </NavLink>
                <NavLink 
                  to="/about-us" 
                  className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  {t('nav.aboutUs')}
                </NavLink>
                <NavLink 
                  to="/contact" 
                  className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  {t('nav.contact')}
                </NavLink>
                <NavLink 
                  to="/dashboard" 
                  className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  {t('nav.dashboard')}
                </NavLink>
                <NavLink 
                  to="/profile" 
                  className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                  onClick={handleNavClick}
                >
                  {t('nav.profile')}
                </NavLink>
                <button 
                  className="mobile-nav-link sign-out"
                  onClick={() => {
                    handleNavClick()
                    handleLogout()
                  }}
                >
                  {t('nav.signOut')}
                </button>
              </>
            )}
          </nav>
        </div>
      </div>
    </>
  )
}

export default Navbar
